import React, { useState } from "react";

import clsx from "clsx";
import { Link } from "react-router-dom";

import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { type DataTypeProvider } from "@devexpress/dx-react-grid";
import { useDesignElementManager } from "@hooks/use-design-element-manager";
import { AdIcon } from "@pages/objects/utils";

export const TouchpointsDataFormatter = ({
  row,
}: DataTypeProvider.ValueFormatterProps) => {
  const touchpoints = row.pathElements;

  const threshold =
    touchpoints[0] && touchpoints[0].label === "Platform (Northbeam)" ? 6 : 3;
  const visibleTouchpoints = touchpoints.slice(0, threshold);
  const moreTouchpoints = touchpoints.slice(threshold);

  const [isMoreVisible, setIsMoreVisible] = useState(false);

  return (
    <div className="flex items-center justify-between h-full">
      <div className="flex gap-2">
        <button
          className={clsx(
            "shrink-0 text-xxs rounded w-6 h-6 flex items-center justify-center",
            {
              "hover:bg-cosmic-3": moreTouchpoints.length > 0,
            },
          )}
          disabled={moreTouchpoints.length === 0}
          onClick={() => setIsMoreVisible(!isMoreVisible)}
        >
          <i
            className={clsx("fa-regular fa-sharp", {
              "fa-chevron-down": !isMoreVisible,
              "fa-chevron-up": isMoreVisible,
              "text-gray": moreTouchpoints.length === 0,
            })}
          />
        </button>
        <div className="font-sans font-normal flex gap-2 flex-wrap items-center">
          {visibleTouchpoints.length === 0 && <>(none)</>}
          {visibleTouchpoints.map((e: any, i: number) => (
            <TouchpointElement key={e.elementId ?? i} touchpoint={e} />
          ))}
          {moreTouchpoints.length > 0 && !isMoreVisible && (
            <div
              className={clsx(
                "border border-gray bg-interstellar-3 rounded text-xs py-[3px] px-2",
              )}
            >
              +{moreTouchpoints.length}
            </div>
          )}
          {isMoreVisible &&
            moreTouchpoints.map((e: any, i: number) => (
              <TouchpointElement key={e.elementId ?? i} touchpoint={e} />
            ))}
        </div>
      </div>
    </div>
  );
};

const TouchpointElement = ({ touchpoint }: { touchpoint: any }) => {
  const { user } = useUser();
  const { designElementManager } = useDesignElementManager();
  const icon = designElementManager
    .getDesignElement(touchpoint.name)
    .getComponent({
      size: "100%",
      className: "shrink-0 w-6 h-6 mr-1",
    });

  if (touchpoint.label === "Platform (Northbeam)") {
    return (
      <GenericTooltip
        noInfoCircle
        content={touchpoint.name}
        mouseEnterDelay={0.5}
      >
        {icon}
      </GenericTooltip>
    );
  } else {
    let cls = "border-venus bg-venus-2";

    if (touchpoint.root || touchpoint.label) {
      cls = "border-cosmic bg-cosmic-3";
    } else if (touchpoint.nbtPlatformID === "direct") {
      cls = "border-pluto bg-pluto-2";
    } else if (touchpoint.platform && touchpoint.platform.includes("Organic")) {
      cls = "border-sun bg-sun-2";
    } else {
      cls = "border-venus bg-venus-2";
    }

    const content = (
      <GenericTooltip
        noInfoCircle
        content={touchpoint.name}
        mouseEnterDelay={0.5}
      >
        <div
          className={clsx(
            "border rounded py-[3px] px-2.5 text-xs",
            "max-w-[120px] truncate flex items-center",
            cls,
          )}
        >
          {touchpoint.nbtPlatformID && (
            <AdIcon platform={touchpoint.nbtPlatformID} sizeInEM={1} />
          )}
          <span className="truncate">{touchpoint.name}</span>
        </div>
      </GenericTooltip>
    );

    return touchpoint.objectId ? (
      <Link
        className="cursor-pointer no-underline"
        to={user.pathFromRoot(`/objects/${touchpoint.objectId}`)}
        target="_blank"
        rel="noreferrer"
      >
        {content}
      </Link>
    ) : (
      content
    );
  }
};
